import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { API_BASE, formatApiError } from "../../components/AuthContext";
import { toast } from "sonner";
import { FileSignature, Check, Loader2, ArrowLeft, Eraser } from "lucide-react";

function fmtDate(iso) { if (!iso) return ""; return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }

export function PortalSignaturesList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API_BASE}/portal/esign`)
      .then((r) => setRows(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className="px-10 py-12 max-w-[1000px]" data-testid="portal-signatures-list">
      <div className="mb-8">
        <div className="overline mb-2 text-[var(--accent)]">From your accountant</div>
        <h1 className="font-heading text-4xl sm:text-5xl leading-tight mb-2" style={{ fontWeight: 500 }}>
          <em className="italic" style={{ color: "var(--accent)" }}>Signatures</em> needed
        </h1>
        <p className="text-[var(--ink-2)] max-w-2xl">
          Documents ready for you to review and sign. Click any document to add your electronic signature.
        </p>
      </div>
      {loading ? (
        <div className="text-[var(--ink-3)]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center">
          <FileSignature size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <div className="font-heading text-xl mb-1" style={{ fontWeight: 500 }}>No signing requests</div>
          <div className="text-[var(--ink-3)] text-sm">Documents requiring your signature will appear here.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((er) => (
            <Link
              key={er.esign_id}
              to={`/portal/signatures/${er.esign_id}`}
              data-testid={`portal-esign-row-${er.esign_id}`}
              className="card p-5 block hover:border-[var(--ink)] transition"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-heading text-lg" style={{ fontWeight: 500 }}>{er.title}</div>
                  <div className="text-[12px] text-[var(--ink-3)] font-mono mt-2">
                    Requested {fmtDate(er.created_at)}
                    {er.signed_at && <> · Signed {fmtDate(er.signed_at)}</>}
                  </div>
                </div>
                <span className="badge" style={{
                  background: er.status === "signed" ? "var(--moss)" : "var(--accent)", color: "var(--paper)"
                }}>{er.status}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function PortalEsignSign() {
  const { id } = useParams();
  const [er, setEr] = useState(null);
  const [typedName, setTypedName] = useState("");
  const [signing, setSigning] = useState(false);
  const canvasRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/portal/esign/${id}`).then((r) => setEr(r.data)).catch(console.error);
  }, [id]);

  // Canvas drawing
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1a1814";
    let drawing = false;
    let last = null;
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const t = e.touches?.[0];
      const x = (t ? t.clientX : e.clientX) - r.left;
      const y = (t ? t.clientY : e.clientY) - r.top;
      return [x * (c.width / r.width), y * (c.height / r.height)];
    };
    const down = (e) => { e.preventDefault(); drawing = true; last = pos(e); };
    const move = (e) => {
      if (!drawing) return;
      e.preventDefault();
      const [x, y] = pos(e);
      ctx.beginPath(); ctx.moveTo(...last); ctx.lineTo(x, y); ctx.stroke();
      last = [x, y]; setHasInk(true);
    };
    const up = () => { drawing = false; last = null; };
    c.addEventListener("mousedown", down); c.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    c.addEventListener("touchstart", down); c.addEventListener("touchmove", move);
    window.addEventListener("touchend", up);
    return () => {
      c.removeEventListener("mousedown", down); c.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      c.removeEventListener("touchstart", down); c.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [er]);

  const clearInk = () => {
    const c = canvasRef.current; if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!typedName.trim() || !hasInk) {
      toast.error("Please type your full name and draw your signature.");
      return;
    }
    const data_url = canvasRef.current.toDataURL("image/png");
    setSigning(true);
    try {
      const r = await axios.post(`${API_BASE}/portal/esign/${id}/sign`, {
        typed_name: typedName.trim(), signature_data_url: data_url,
      });
      setEr(r.data);
      toast.success("Signature captured. Thank you.");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSigning(false);
    }
  };

  if (!er) return <div className="p-12 text-[var(--ink-3)]">Loading…</div>;

  return (
    <div className="px-10 py-12 max-w-[820px]" data-testid="portal-esign-page">
      <Link to="/portal/signatures" className="inline-flex items-center gap-1 text-[12px] text-[var(--ink-3)] hover:text-[var(--ink)] mb-4">
        <ArrowLeft size={12} /> Back to signatures
      </Link>
      <div className="overline mb-2">Signature requested</div>
      <h1 className="font-heading text-3xl mb-4" style={{ fontWeight: 500 }}>{er.title}</h1>
      <div className="card p-6 mb-6">
        <div className="whitespace-pre-wrap text-[14px] leading-relaxed">{er.body_md}</div>
      </div>

      {er.status === "signed" ? (
        <div className="card p-6 text-center" data-testid="portal-esign-signed">
          <Check size={28} className="mx-auto mb-3" style={{ color: "var(--moss)" }} />
          <div className="font-heading text-xl mb-1" style={{ fontWeight: 500 }}>Signed</div>
          <div className="text-[var(--ink-3)] text-sm">
            By {er.signed_name} on {fmtDate(er.signed_at)}
          </div>
          {er.signature_data_url && (
            <img alt="signature" src={er.signature_data_url}
                 className="mt-4 mx-auto border border-[var(--border)] rounded-md bg-white"
                 style={{ maxWidth: 320 }} />
          )}
          <div className="text-[11px] font-mono text-[var(--ink-3)] mt-3">
            IP: {er.signed_ip} · Audit captured.
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="card p-6 space-y-4" data-testid="portal-esign-form">
          <div>
            <label className="overline block mb-1.5">Type your full name</label>
            <input
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              required minLength={2}
              data-testid="portal-esign-typed-name"
              className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="overline">Draw your signature</label>
              <button type="button" onClick={clearInk} data-testid="portal-esign-clear"
                      className="text-[11px] text-[var(--ink-3)] hover:text-[var(--accent)] inline-flex items-center gap-1">
                <Eraser size={11} /> Clear
              </button>
            </div>
            <canvas
              ref={canvasRef}
              width={720} height={180}
              data-testid="portal-esign-canvas"
              className="w-full border border-[var(--border)] rounded-md bg-white cursor-crosshair touch-none"
              style={{ height: 180 }}
            />
            <div className="text-[11px] text-[var(--ink-3)] mt-2">
              By clicking "Sign now" you agree your typed name and drawn signature constitute your legal signature on this document.
              Your IP, timestamp, and user-agent will be recorded as the audit trail.
            </div>
          </div>
          <button type="submit" disabled={signing} className="btn-primary w-full justify-center" data-testid="portal-esign-submit">
            {signing ? <Loader2 size={14} className="animate-spin" /> : <FileSignature size={14} />}
            {signing ? "Signing…" : "Sign now"}
          </button>
        </form>
      )}
    </div>
  );
}
